import React, { memo } from 'react';
import { ZapIcon, GiftIcon } from 'lucide-react';
export function WhatYouGet() {
  return <section id="what-you-get" className="bg-[#1A1A2E] py-24 px-6" data-id="element-240">
      <div className="max-w-6xl mx-auto" data-id="element-241">
        {/* Header */}
        <div className="text-center mb-6" data-id="element-242">
          <p className="text-[#4361EE] text-sm font-bold tracking-[0.3em] uppercase mb-4" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-243">
            The Complete System
          </p>
          <h2 className="text-[56px] sm:text-[72px] lg:text-[88px] text-white leading-none mb-4" style={{
          fontFamily: "'Bebas Neue', sans-serif"
        }} data-id="element-244">
            EVERYTHING INSIDE{' '}
            <span className="text-[#4361EE] glow-text-blue" data-id="element-245">ADHD RESET</span>
          </h2>
          <p className="text-[#A0AEC0] text-xl" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-246">
            <strong className="text-white" data-id="element-247">7 modules.</strong>{' '}
            <strong className="text-white" data-id="element-248">47 strategies.</strong> Zero fluff.
          </p>
        </div>

        {/* Module grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16 mt-12" data-id="element-249">
          <ModuleCard number="01" title="The ADHD Brain Decoded" description="Understand exactly why your brain works the way it does. Stop fighting it — start leveraging it. Includes the 3-minute morning brain-state check." data-id="element-250" />
          <ModuleCard number="02" title="The Focus Architecture" description="Build an environment and schedule that makes focus the path of least resistance. The exact desk setup, app stack, and time-blocking method." data-id="element-251" />
          <ModuleCard number="03" title="Task Gravity System" description="The prioritization framework designed for ADHD brains. Never stare at your to-do list paralyzed again. Know exactly what to do next, always." data-id="element-252" />
          <ModuleCard number="04" title="Hyperfocus Hacking" description="Turn your biggest 'weakness' into your superpower. Learn to summon hyperfocus on demand and channel it toward what actually matters." data-id="element-253" />
          <ModuleCard number="05" title="The Anti-Procrastination Protocol" description="The 5-step system to start any task in under 2 minutes. Includes the 'body double' technique and the 'minimum viable start' framework." data-id="element-254" />
          <ModuleCard number="06" title="Memory & Working Memory Hacks" description="External brain systems that mean you never drop the ball again. The capture-clarify-connect method for ADHD working memory." data-id="element-255" />
          <ModuleCard number="07" title="The Consistency Code" description="How to build habits that actually stick when you have ADHD. The 'minimum viable habit' approach and identity-based behavior change." data-id="element-256" />
        </div>

        {/* Bonuses */}
        <div className="rounded-2xl p-8 border border-[#FFD60A]/30" style={{
        background: 'linear-gradient(135deg, rgba(255, 214, 10, 0.05) 0%, rgba(67, 97, 238, 0.05) 100%)'
      }} data-id="element-257">
          <div className="flex items-center gap-3 mb-6" data-id="element-258">
            <GiftIcon className="w-6 h-6 text-[#FFD60A]" data-id="element-259" />
            <h3 className="text-[#FFD60A] text-[36px] sm:text-[44px] leading-none glow-text-yellow" style={{
            fontFamily: "'Bebas Neue', sans-serif"
          }} data-id="element-260">
              PLUS THESE FREE BONUSES:
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-261">
            <BonusItem title="The ADHD Daily Planner Template" value="$47 value" description="A Notion template pre-built for ADHD brains. Drag, drop, done." data-id="element-262" />
            <BonusItem title="Crisis Mode Cheat Sheet" value="$27 value" description="A one-page emergency protocol for when everything feels impossible." data-id="element-263" />
            <BonusItem title="The ADHD Partner Guide" value="$37 value" description="Help your partner understand your brain. Reduce friction, increase support." data-id="element-264" />
          </div>
        </div>
      </div>
    </section>;
}
type ModuleCardProps = {
  number: string;
  title: string;
  description: string;
};
function ModuleCard({
  number,
  title,
  description
}: ModuleCardProps) {
  return <div className="relative rounded-xl p-6 border border-white/5 group hover:border-[#4361EE]/40 transition-all duration-300" style={{
    background: '#16213E'
  }} data-id="element-265">
      {/* Left accent border */}
      <div className="absolute left-0 top-4 bottom-4 w-1 rounded-full bg-[#4361EE] opacity-60 group-hover:opacity-100 transition-opacity" data-id="element-266" />

      <div className="flex items-start gap-5" data-id="element-267">
        {/* Module number */}
        <span className="text-[#4361EE] text-[52px] leading-none flex-shrink-0 glow-text-blue" style={{
        fontFamily: "'Bebas Neue', sans-serif"
      }} data-id="element-268">
          {number}
        </span>

        <div data-id="element-269">
          <h3 className="text-white font-bold text-lg mb-2 leading-tight" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-270">
            {title}
          </h3>
          <p className="text-[#A0AEC0] text-sm leading-relaxed" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-271">
            {description}
          </p>
        </div>
      </div>
    </div>;
}
type BonusItemProps = {
  title: string;
  value: string;
  description: string;
};
function BonusItem({
  title,
  value,
  description
}: BonusItemProps) {
  return <div className="flex flex-col gap-2" data-id="element-272">
      <div className="flex items-start gap-2" data-id="element-273">
        <ZapIcon className="w-4 h-4 text-[#FFD60A] flex-shrink-0 mt-0.5" data-id="element-274" />
        <div data-id="element-275">
          <p className="text-white font-bold text-sm leading-tight" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-276">
            {title}
          </p>
          <span className="text-[#FFD60A] text-xs font-bold" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-277">
            {value}
          </span>
        </div>
      </div>
      <p className="text-[#A0AEC0] text-xs leading-relaxed pl-6" style={{
      fontFamily: "'Inter', sans-serif"
    }} data-id="element-278">
        {description}
      </p>
    </div>;
}